import React, { useState } from 'react';
import { Flex, Gutter, Title, BodyText, GridBag, RadioButton } from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import { AdminProductTypeEnum, LocaleResponse } from '@cdaas/api-clients/dist/product-service-openapi';

import EditableItem from '../../../../shared/EditableItem/EditableItem';
import { updateEntityByIndex } from '../../../../utils/entities';
import { Errors } from '../utils';

interface GroupNameProps {
  title: string;
  type: AdminProductTypeEnum | undefined;
  name: string | undefined;
  brand: string | undefined;
  nameTranslations: { localeId: number; translation: string }[] | undefined;
  locale: LocaleResponse;
  handleTypeChange: (type: AdminProductTypeEnum) => void;
  handleNameChange: (name: string) => void;
  handleNameTranslationsChange: (translations: { localeId: number; translation: string }[]) => void;
  handleBrandChange: (brand: string) => void;
  nameError: Errors['name'];
  brandError: Errors['brand'];
}

function GroupName(props: GroupNameProps) {
  const {
    title,
    type,
    name,
    brand,
    nameTranslations,
    locale,
    handleTypeChange,
    handleNameChange,
    handleNameTranslationsChange,
    handleBrandChange,
    nameError,
    brandError,
  } = props;
  const { t } = useTranslation('productscreen');

  const [nameReferenceInputOpen, setNameReferenceInputOpen] = useState<boolean>(false);

  const handleNameTranslationChange = (nameTranslation: string) => {
    const index = (nameTranslations || []).findIndex(translation => translation.localeId === locale.id);
    if (index !== -1 && nameTranslations) {
      const updatedTranslation = { ...nameTranslations[index], translation: nameTranslation };
      const updatedTranslations = updateEntityByIndex(nameTranslations, index, updatedTranslation);
      handleNameTranslationsChange(updatedTranslations);
    }
  };

  const currentNameTranslation = nameTranslations?.find(translation => translation.localeId === locale.id)?.translation;

  return (
    <Flex directionColumn>
      <Title size="md">{title}</Title>
      <Gutter />
      <BodyText color="gray400">{t('group_name_section.info')}</BodyText>
      <Gutter />
      <GridBag>
        <GridBag.Item md={4}>
          <EditableItem
            divProps={{
              padding: { right: 'md' },
            }}
          >
            <EditableItem.Input
              value={currentNameTranslation}
              edit={true}
              inputProps={{
                name: 'groupNameTranslation',
                onBlur: (e: React.FocusEvent<HTMLInputElement>) => handleNameTranslationChange(e.target.value),
                placeholder: t('group_name_section.group_translation_placeholder', { language: locale.name.split(' ')[0] }),
                label: t('group_name_section.group_name_translation_label'),
                onKeyDown: () => undefined,
              }}
            />
            <Gutter size="sm" />
            <EditableItem.Input
              value={name}
              edit={nameReferenceInputOpen}
              isReference={true}
              focusOnEdit={true}
              inputProps={{
                name: 'groupName',
                onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                  handleNameChange(e.target.value);
                  setNameReferenceInputOpen(false);
                },
                placeholder: t('group_name_section.group_reference_placeholder'),
                label: t('group_name_section.group_name_reference_label'),
                onKeyDown: () => undefined,
                required: true,
                errorText: nameError,
              }}
              editIconProps={{
                'data-testid': `group-name-edit`,
                onClick: () => setNameReferenceInputOpen(true),
              }}
            />
          </EditableItem>
        </GridBag.Item>
        <GridBag.Item md={4}>
          <EditableItem>
            <EditableItem.Input
              value={brand}
              edit={true}
              inputProps={{
                name: 'brand',
                onBlur: (event: React.FocusEvent<HTMLInputElement>) => handleBrandChange(event.target.value),
                placeholder: t('group_name_section.brand_placeholder'),
                label: t('group_name_section.brand_label'),
                onKeyDown: () => undefined,
                required: true,
                errorText: brandError,
              }}
            />
          </EditableItem>
        </GridBag.Item>
      </GridBag>
      <Gutter size="xlg" />
      <RadioButton
        name={'product-type-hardware-checkbox'}
        label={
          <>
            {t('group_name_section.product_type_hardware')}{' '}
            <span style={{ fontStyle: 'italic' }}>{t('group_name_section.product_type_hardware_info')}</span>
          </>
        }
        checked={type === AdminProductTypeEnum.Hardware}
        onChange={() => handleTypeChange(AdminProductTypeEnum.Hardware)}
      />
      <Gutter size="sm" />
      <RadioButton
        name={'product-type-service-checkbox'}
        label={
          <>
            {t('group_name_section.product_type_service')}{' '}
            <span style={{ fontStyle: 'italic' }}>{t('group_name_section.product_type_service_info')}</span>
          </>
        }
        checked={type === AdminProductTypeEnum.Service}
        onChange={() => handleTypeChange(AdminProductTypeEnum.Service)}
      />
      <Gutter size="sm" />
    </Flex>
  );
}

export default React.memo(GroupName);
